import { useMemo, useState } from 'react';
import type { UIMessage } from 'ai';
import type { PendingApproval } from '../api/pending-approvals.api';
import { pendingApprovalsOf } from '../lib/streamed-pending-approvals';

/** A conversation its host does not keep, whose pause is read off what it streamed. */
export interface StreamedPause {
  messages: readonly UIMessage[];
  agent: Pick<PendingApproval, 'agentId' | 'agentName'>;
  toolLabel: (toolName: string) => string;
}

const NONE: PendingApproval[] = [];

/** `usePendingApprovals`, for a conversation whose pause lives only in its messages. */
export function useStreamedPendingApprovals(streamed: StreamedPause | undefined, busy: boolean) {
  const approvals = useMemo(
    () =>
      streamed && !busy
        ? pendingApprovalsOf(streamed.messages, streamed.agent, streamed.toolLabel)
        : NONE,
    [streamed, busy],
  );
  // The messages keep the pause until the resumed turn streams over it, so an answered one is
  // hidden by key rather than waiting for them to change.
  const [answered, setAnswered] = useState<string>();
  // Once the resumed turn ends, the messages say whether the pause was answered: a turn that
  // failed or was stopped first leaves it in them, and it must be asked again.
  const [wasBusy, setWasBusy] = useState(busy);
  if (wasBusy !== busy) {
    setWasBusy(busy);
    if (!busy) setAnswered(undefined);
  }
  const pauseKey = approvals.map((approval) => approval.approvalId).join(',');

  return {
    approvals: pauseKey && pauseKey === answered ? NONE : approvals,
    clear: () => setAnswered(pauseKey),
    refetch: () => setAnswered(undefined),
  };
}
